import type { Car, Track } from "@vms/types";
import { useQuery, type QueryState } from "./queries";

/**
 * Org-scoped reads of the cloud's hub-mirrored content tables. Backs the
 * cross-venue leaderboard form (track / car / event pickers) and the venue
 * leaderboard detail modal (label resolution).
 *
 * Each track / car carries `availableInVenues: string[]` — the venue IDs that
 * have the asset installed. Surfaced as a "in N of M venues" badge in the
 * shared `TrackPicker` / `CarMultiSelect` components when the operator's
 * venue scope is wider than the asset's coverage.
 */

export interface OrgTrack extends Track {
  availableInVenues: string[];
}

export interface OrgCar extends Car {
  availableInVenues: string[];
}

export interface OrgEvent {
  id: string;
  name: string;
  venueId: string;
  venueName: string | null;
  game: string | null;
  mode: string | null;
  status: string | null;
  track: string | null;
  trackName: string | null;
  carsAvailable: string[];
  carNames: string[];
  startDate: string | null;
  createdAt: string;
  updatedAt: string;
}

function buildContentEndpoint(
  base: string,
  organisationId: string | undefined,
  params: { game?: string | null; venueIds?: string[] | null },
): string | null {
  if (!organisationId) return null;
  const q = new URLSearchParams();
  if (params.game) q.set("game", params.game);
  if (params.venueIds && params.venueIds.length > 0) {
    q.set("venueIds", params.venueIds.join(","));
  }
  const qs = q.toString();
  return `/api/organisations/${organisationId}/${base}${qs ? `?${qs}` : ""}`;
}

export function useOrgTracks(
  organisationId: string | undefined,
  game: string | null | undefined,
  venueIds?: string[] | null,
): QueryState<OrgTrack[]> {
  const endpoint = game
    ? buildContentEndpoint("tracks", organisationId, { game, venueIds })
    : null;
  return useQuery<OrgTrack[]>(endpoint);
}

export function useOrgCars(
  organisationId: string | undefined,
  game: string | null | undefined,
  venueIds?: string[] | null,
): QueryState<OrgCar[]> {
  const endpoint = game
    ? buildContentEndpoint("cars", organisationId, { game, venueIds })
    : null;
  return useQuery<OrgCar[]>(endpoint);
}

export function useOrgEvents(
  organisationId: string | undefined,
  venueIds?: string[] | null,
  game?: string | null,
): QueryState<OrgEvent[]> {
  const endpoint = buildContentEndpoint("events", organisationId, { game, venueIds });
  return useQuery<OrgEvent[]>(endpoint);
}
